import type {
  ActionContext,
  ActionTrigger,
  DisplayMode,
  DisplayModeConfig,
  EventCallback,
  MenuAction,
  MenuActionInput,
  ResolvedDisplayMode,
  SheetSide,
  WidgetError,
  WidgetErrorCode,
  WidgetEvent,
  WidgetPreferences,
} from './types'

export type WidgetView =
  | 'home'
  | 'select-project'
  | 'new-ticket'
  | 'my-tickets'
  | 'ticket-detail'
  | 'track'
  | 'kb'
  | 'preferences'
  | { kind: 'custom-action'; id: string }

export interface WidgetRegistrySnapshot {
  isOpen: boolean
  currentView: WidgetView
  currentDisplayMode: ResolvedDisplayMode
  actions: MenuAction[]
  preferences: WidgetPreferences
  activeActionId: string | null
  previousDisplayMode: ResolvedDisplayMode | null
  // Context fields for ActionContext derivation
  user: { email?: string; name?: string } | null
  projectId: string
  locale: string
}

export interface RegistryContextSeed {
  user?: { email?: string; name?: string } | null
  projectId?: string
  locale?: string
}

export type DisplayChangeReason =
  | 'programmatic'
  | 'per-action'
  | 'per-action-restore'
  | 'preferences'

const ACTION_ID_PATTERN = /^[a-z][a-z0-9-]{0,63}$/
const BUILT_IN_IDS: readonly string[] = [
  'new-ticket',
  'my-tickets',
  'track',
  'preferences',
  'select-project',
]

function defaultDisplayMode(): ResolvedDisplayMode {
  return { mode: 'popover', side: 'end', dismissOnBackdrop: true }
}

function resolveModeFromConfig(cfg: DisplayModeConfig | undefined): ResolvedDisplayMode | null {
  if (!cfg || !cfg.mode) return null
  const mode = cfg.mode
  const side: SheetSide = cfg.side ?? 'end'
  const width = cfg.width ?? (mode === 'side-sheet' ? '420px' : undefined)
  const height = cfg.height ?? (mode === 'bottom-sheet' ? '55vh' : undefined)
  const dismissOnBackdrop = cfg.dismissOnBackdrop ?? true
  return { mode, side, width, height, dismissOnBackdrop }
}

export class WidgetRegistry {
  private snapshot: WidgetRegistrySnapshot
  private listeners = new Set<(snapshot: WidgetRegistrySnapshot) => void>()
  private eventListeners: Map<WidgetEvent, Set<EventCallback>> = new Map()
  private activatingElementRef: WeakRef<Element> | null = null
  private menuCloseOnAction: boolean = true
  private hostOnPreferencesChange: ((next: WidgetPreferences) => void) | undefined
  private initialPreferences: WidgetPreferences = {}
  private configDisplay: DisplayModeConfig | undefined

  constructor() {
    this.snapshot = {
      isOpen: false,
      currentView: 'home',
      currentDisplayMode: defaultDisplayMode(),
      actions: [],
      preferences: {},
      activeActionId: null,
      previousDisplayMode: null,
      user: null,
      projectId: '_current',
      locale: 'en',
    }

    this.getSnapshot = this.getSnapshot.bind(this)
    this.subscribe = this.subscribe.bind(this)
    this.on = this.on.bind(this)
    this.off = this.off.bind(this)
    this.open = this.open.bind(this)
    this.close = this.close.bind(this)
    this.toggle = this.toggle.bind(this)
    this.openMenu = this.openMenu.bind(this)
    this.openAction = this.openAction.bind(this)
    this.setDisplayMode = this.setDisplayMode.bind(this)
    this.setPreferences = this.setPreferences.bind(this)
    this.getPreferences = this.getPreferences.bind(this)
    this.addAction = this.addAction.bind(this)
    this.removeAction = this.removeAction.bind(this)
    this.recordActivator = this.recordActivator.bind(this)
    this.restoreFocus = this.restoreFocus.bind(this)
  }

  // ─── Snapshot / subscribe (useSyncExternalStore contract) ────────────────────

  getSnapshot(): WidgetRegistrySnapshot {
    return this.snapshot
  }

  subscribe(listener: (snapshot: WidgetRegistrySnapshot) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private updateSnapshot(partial: Partial<WidgetRegistrySnapshot>): void {
    this.snapshot = { ...this.snapshot, ...partial }
    this.emitChange()
  }

  private emitChange(): void {
    for (const listener of this.listeners) {
      try { listener(this.snapshot) } catch { /* ignore listener errors */ }
    }
  }

  // ─── Event stream ────────────────────────────────────────────────────────────

  on(event: WidgetEvent, cb: EventCallback): () => void {
    let set = this.eventListeners.get(event)
    if (!set) {
      set = new Set()
      this.eventListeners.set(event, set)
    }
    set.add(cb)
    return () => {
      set!.delete(cb)
    }
  }

  off(event: WidgetEvent, cb: EventCallback): void {
    this.eventListeners.get(event)?.delete(cb)
  }

  private emit(event: WidgetEvent, data?: unknown): void {
    const set = this.eventListeners.get(event)
    if (!set) return
    for (const cb of set) {
      try { cb(data) } catch { /* ignore listener errors */ }
    }
  }

  // Public emit for legacy vanilla flow paths (ticket:created, reply:sent, error from existing UI
  // builders) that pre-date the registry but should still fan out to registry.on() subscribers.
  emitEvent(event: WidgetEvent, data?: unknown): void {
    this.emit(event, data)
  }

  private emitError(code: WidgetErrorCode, message: string, actionId?: string, cause?: unknown): void {
    const err: WidgetError = { code, message }
    if (actionId !== undefined) err.actionId = actionId
    if (cause !== undefined) err.cause = cause
    this.emit('error', err)
  }

  // ─── Configuration seeding ───────────────────────────────────────────────────

  setContext(ctx: RegistryContextSeed): void {
    const partial: Partial<WidgetRegistrySnapshot> = {}
    if (ctx.user !== undefined) partial.user = ctx.user
    if (ctx.projectId !== undefined) partial.projectId = ctx.projectId
    if (ctx.locale !== undefined) partial.locale = ctx.locale
    if (Object.keys(partial).length > 0) this.updateSnapshot(partial)
  }

  setMenuCloseOnAction(value: boolean): void {
    this.menuCloseOnAction = value
  }

  setHostPreferencesCallback(cb: ((next: WidgetPreferences) => void) | undefined): void {
    this.hostOnPreferencesChange = cb
  }

  setApiKeyForStorage(_apiKey: string | null): void {
    // Reserved hook for future registry-side preference persistence. Storage writes currently
    // flow through resolvePreferences() / writePreferences() in storage.ts, keyed by apiKey at
    // the call site — so the registry does not need to retain it. Kept as a stable setter so
    // callers don't need to branch on SDK version.
  }

  setInitialPreferences(initial: WidgetPreferences): void {
    this.initialPreferences = initial
  }

  setConfigDisplay(cfg: DisplayModeConfig | undefined): void {
    this.configDisplay = cfg
    // Re-resolve current display mode in case we're not inside a per-action override
    if (this.snapshot.activeActionId === null) {
      this.updateSnapshot({ currentDisplayMode: this.resolveEffectiveDisplayMode() })
    }
  }

  initPreferencesSnapshot(preferences: WidgetPreferences): void {
    this.updateSnapshot({
      preferences,
      currentDisplayMode: this.resolveEffectiveDisplayMode(preferences),
    })
  }

  hasInitialPreferences(): boolean {
    return Object.keys(this.initialPreferences).length > 0
  }

  getInitialPreferences(): WidgetPreferences {
    return this.initialPreferences
  }

  // ─── Action registration ─────────────────────────────────────────────────────

  private normalizeAction(input: MenuActionInput): MenuAction | null {
    if (!input || typeof input !== 'object') {
      this.emitError('action-missing-handler', 'Action input must be an object')
      return null
    }

    const id = input.id
    if (!id || typeof id !== 'string' || !ACTION_ID_PATTERN.test(id)) {
      this.emitError(
        'action-missing-handler',
        `Action id "${String(id)}" is invalid. Expected pattern ^[a-z][a-z0-9-]{0,63}$`,
        typeof id === 'string' ? id : undefined,
      )
      return null
    }

    if (this.snapshot.actions.some(a => a.id === id)) {
      this.emitError('action-id-conflict', `Action "${id}" is already registered`, id)
      return null
    }

    const hasHandler = typeof input.onClick === 'function'
    const hasTrigger = !!input.trigger
    if (!hasHandler && !hasTrigger) {
      this.emitError('action-missing-handler', `Action "${id}" requires onClick or trigger`, id)
      return null
    }

    // Handler-wins precedence (FR-002): if both present, drop trigger and log debug.
    let trigger: ActionTrigger | undefined = input.trigger
    if (hasHandler && hasTrigger) {
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug(
          `[reqdesk] action "${id}" has both onClick and trigger; handler takes precedence (trigger ignored)`,
        )
      }
      trigger = undefined
    }

    const section: 'top' | 'bottom' = input.section ?? 'bottom'
    const normalized: MenuAction = {
      id,
      label: input.label,
      description: input.description,
      icon: input.icon,
      section,
      anchor: input.anchor,
      closeOnClick: input.closeOnClick,
      onClick: input.onClick,
      trigger,
      visible: input.visible,
      badge: input.badge ?? null,
      display: input.display,
    }
    return normalized
  }

  addAction(input: MenuActionInput): () => void {
    const normalized = this.normalizeAction(input)
    if (!normalized) return () => { /* rejected — no-op unsubscribe */ }
    const nextActions = this.orderActions([...this.snapshot.actions, normalized])
    this.updateSnapshot({ actions: nextActions })
    return () => { this.removeAction(normalized.id) }
  }

  removeAction(id: string): void {
    const nextActions = this.snapshot.actions.filter(a => a.id !== id)
    if (nextActions.length !== this.snapshot.actions.length) {
      this.updateSnapshot({ actions: nextActions })
    }
  }

  // Order by: anchor.before/after referencing built-ins or other actions (before wins),
  // then by section, then by original insertion order.
  private orderActions(actions: MenuAction[]): MenuAction[] {
    // Preserve insertion order as the base. Custom actions anchor relative to built-ins or each
    // other. The built-in list is known (see BUILT_IN_IDS). When resolving anchors we only know
    // custom actions here — the actual render layer weaves them with built-ins.
    return actions.slice()
  }

  // Called by render layer. Given an ordered built-in list, weaves custom actions by anchor.
  resolveRenderOrder(builtInIds: readonly string[]): Array<{ kind: 'built-in' | 'custom'; id: string }> {
    const result: Array<{ kind: 'built-in' | 'custom'; id: string }> = []
    const custom = this.snapshot.actions
    const placed = new Set<string>()

    // Helper: insert custom items anchored before/after a specific id
    const insertAnchored = (anchorId: string, slotIndex: number) => {
      const before = custom.filter(a => !placed.has(a.id) && a.anchor?.before === anchorId)
      for (const a of before) {
        result.splice(slotIndex++, 0, { kind: 'custom', id: a.id })
        placed.add(a.id)
      }
    }
    const appendAfterAnchored = (anchorId: string) => {
      const after = custom.filter(a => !placed.has(a.id) && !a.anchor?.before && a.anchor?.after === anchorId)
      for (const a of after) {
        result.push({ kind: 'custom', id: a.id })
        placed.add(a.id)
      }
    }

    // Build by walking the built-ins, interleaving anchored customs.
    for (const builtInId of builtInIds) {
      insertAnchored(builtInId, result.length)
      result.push({ kind: 'built-in', id: builtInId })
      appendAfterAnchored(builtInId)
    }

    // Any remaining customs (no anchor, or anchor not found) go at the end in insertion order,
    // grouped by section. 'top' items come first conceptually, but since built-ins are already in
    // place, we append unanchored 'top' items above the list by section; 'bottom' items at the end.
    const unplacedTop: MenuAction[] = []
    const unplacedBottom: MenuAction[] = []
    for (const a of custom) {
      if (placed.has(a.id)) continue
      if (a.section === 'top') unplacedTop.push(a)
      else unplacedBottom.push(a)
    }
    // Prepend 'top' section items (insertion order preserved).
    for (let i = 0; i < unplacedTop.length; i++) {
      result.splice(i, 0, { kind: 'custom', id: unplacedTop[i].id })
      placed.add(unplacedTop[i].id)
    }
    for (const a of unplacedBottom) {
      result.push({ kind: 'custom', id: a.id })
      placed.add(a.id)
    }
    return result
  }

  getActionById(id: string): MenuAction | undefined {
    return this.snapshot.actions.find(a => a.id === id)
  }

  // ─── Badge mutation ──────────────────────────────────────────────────────────

  setBadge(actionId: string, value: string | number | null): void {
    const idx = this.snapshot.actions.findIndex(a => a.id === actionId)
    if (idx === -1) return
    const nextActions = this.snapshot.actions.slice()
    nextActions[idx] = { ...nextActions[idx], badge: value }
    this.updateSnapshot({ actions: nextActions })
  }

  // ─── Open / close / toggle ───────────────────────────────────────────────────

  open(): void {
    if (this.snapshot.isOpen) return
    this.updateSnapshot({ isOpen: true })
    this.emit('open')
  }

  close(): void {
    if (!this.snapshot.isOpen) return
    // Restore display mode if a per-action override is active
    const prev = this.snapshot.previousDisplayMode
    const partial: Partial<WidgetRegistrySnapshot> = {
      isOpen: false,
      activeActionId: null,
      previousDisplayMode: null,
      currentView: 'home',
    }
    if (prev) {
      partial.currentDisplayMode = prev
      this.emit('display-mode:changed', { mode: prev.mode, side: prev.side, reason: 'per-action-restore' })
    }
    this.updateSnapshot(partial)
    this.restoreFocus()
    this.emit('close')
  }

  toggle(): void {
    if (this.snapshot.isOpen) this.close()
    else this.open()
  }

  openMenu(): void {
    this.updateSnapshot({ currentView: 'home' })
    if (!this.snapshot.isOpen) this.open()
    this.emit('menu:opened')
  }

  openAction(id: string, input?: unknown): void {
    const action = this.getActionById(id)
    if (!action) {
      this.emitError('action-not-found', `Action "${id}" is not registered`, id)
      return
    }
    if (!this.snapshot.isOpen) this.open()

    // Apply per-action display override
    if (action.display) {
      const override = resolveModeFromConfig(action.display)
      if (override) {
        this.updateSnapshot({
          previousDisplayMode: this.snapshot.previousDisplayMode ?? this.snapshot.currentDisplayMode,
          currentDisplayMode: override,
          activeActionId: action.id,
        })
        this.emit('display-mode:changed', { mode: override.mode, side: override.side, reason: 'per-action' })
      }
    }

    // Invoke handler or dispatch trigger
    if (action.onClick) {
      this.invokeHandler(action, input)
    } else if (action.trigger) {
      this.dispatchTrigger(action, input)
    }
  }

  // ─── Handler invocation (T020) ───────────────────────────────────────────────

  private invokeHandler(action: MenuAction, input: unknown): void {
    let keepOpenFlag = false
    const ctx: ActionContext = {
      actionId: action.id,
      input,
      close: () => this.close(),
      keepOpen: () => { keepOpenFlag = true },
      setBadge: (v) => this.setBadge(action.id, v),
      setLoading: (_loading: boolean) => { /* reserved for future UI */ },
      user: this.snapshot.user,
      projectId: this.snapshot.projectId,
      locale: this.snapshot.locale,
      openAction: (nextId: string, nextInput?: unknown) => this.openAction(nextId, nextInput),
    }

    this.emit('action:triggered', { actionId: action.id, viaTrigger: false, input })

    try {
      const ret = action.onClick!(ctx)
      if (ret && typeof (ret as Promise<void>).then === 'function') {
        (ret as Promise<void>).then(
          () => { /* noop */ },
          (cause: unknown) => {
            this.emitError('action-handler-failed', `Handler for "${action.id}" rejected`, action.id, cause)
          },
        )
      }
    } catch (cause) {
      this.emitError('action-handler-failed', `Handler for "${action.id}" threw`, action.id, cause)
    }

    // Schedule close behavior on the next microtask — handlers that call keepOpen synchronously
    // suppress this. Async handlers do NOT block close.
    queueMicrotask(() => this.applyCloseBehavior(action, keepOpenFlag))
  }

  private applyCloseBehavior(action: MenuAction, keepOpenFlag: boolean): void {
    if (keepOpenFlag) return
    const shouldClose = action.closeOnClick ?? this.menuCloseOnAction
    if (shouldClose) this.close()
  }

  // ─── Trigger dispatch (T019) ─────────────────────────────────────────────────

  private dispatchTrigger(action: MenuAction, input: unknown): void {
    const trigger = action.trigger!
    this.emit('action:triggered', { actionId: action.id, viaTrigger: true, input })
    switch (trigger.kind) {
      case 'custom-event':
        this.resolveCustomEvent(trigger, input)
        break
      case 'call-global':
        this.resolveCallGlobal(action.id, trigger, input)
        break
      case 'url':
        this.resolveUrl(trigger, input)
        break
    }
    queueMicrotask(() => this.applyCloseBehavior(action, false))
  }

  private resolveCustomEvent(trigger: Extract<ActionTrigger, { kind: 'custom-event' }>, input: unknown): void {
    const originalDetail = (trigger.detail as Record<string, unknown> | undefined) ?? undefined
    let detail: unknown
    if (input && typeof input === 'object' && !Array.isArray(input)) {
      detail = { ...(originalDetail ?? {}), ...(input as Record<string, unknown>) }
    } else if (input !== undefined) {
      detail = originalDetail ? { ...originalDetail, input } : { input }
    } else {
      detail = originalDetail
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(trigger.name, { detail }))
    }
  }

  private resolveCallGlobal(
    actionId: string,
    trigger: Extract<ActionTrigger, { kind: 'call-global' }>,
    input: unknown,
  ): void {
    if (typeof window === 'undefined') return
    const parts = trigger.path.split('.')
    let target: unknown = window
    for (const segment of parts) {
      if (target && typeof target === 'object' && segment in (target as Record<string, unknown>)) {
        target = (target as Record<string, unknown>)[segment]
      } else {
        this.emitError(
          'action-trigger-not-found',
          `Global path "${trigger.path}" did not resolve`,
          actionId,
        )
        return
      }
    }
    if (typeof target !== 'function') {
      this.emitError(
        'action-trigger-not-found',
        `Global path "${trigger.path}" did not resolve to a function`,
        actionId,
      )
      return
    }
    const args = trigger.args ? trigger.args.slice() : []
    if (input !== undefined) args[0] = input
    try {
      (target as (...xs: unknown[]) => unknown)(...args)
    } catch (cause) {
      this.emitError('action-handler-failed', `Global "${trigger.path}" threw`, actionId, cause)
    }
  }

  private resolveUrl(trigger: Extract<ActionTrigger, { kind: 'url' }>, input: unknown): void {
    if (typeof window === 'undefined') return
    let href = trigger.href
    if (input !== undefined) {
      try {
        const params = new URLSearchParams()
        if (input && typeof input === 'object') {
          for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
            params.set(k, String(v))
          }
        } else {
          params.set('input', String(input))
        }
        href += (href.includes('?') ? '&' : '?') + params.toString()
      } catch { /* ignore serialization errors */ }
    }
    const target = trigger.target ?? '_self'
    if (target === '_blank') {
      window.open(href, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = href
    }
  }

  // ─── Display mode ────────────────────────────────────────────────────────────

  setDisplayMode(mode: DisplayMode, side?: SheetSide): void {
    const cfg: DisplayModeConfig = { mode, side }
    const resolved = resolveModeFromConfig(cfg) ?? defaultDisplayMode()
    // If inside a per-action override flow, update previousDisplayMode to the new mode so close()
    // restores to the most recent user choice, not the stale original.
    const isInOverride = this.snapshot.activeActionId !== null
    const partial: Partial<WidgetRegistrySnapshot> = { currentDisplayMode: resolved }
    if (isInOverride) partial.previousDisplayMode = resolved
    this.updateSnapshot(partial)
    this.emit('display-mode:changed', { mode: resolved.mode, side: resolved.side, reason: 'programmatic' })
  }

  private resolveEffectiveDisplayMode(preferences?: WidgetPreferences): ResolvedDisplayMode {
    const prefs = preferences ?? this.snapshot.preferences
    // Precedence (lowest to highest): built-in default → config → preferences
    let resolved = defaultDisplayMode()
    const fromConfig = resolveModeFromConfig(this.configDisplay)
    if (fromConfig) resolved = fromConfig
    const fromPrefs = resolveModeFromConfig(prefs.display)
    if (fromPrefs) resolved = fromPrefs
    return resolved
  }

  // ─── Preferences ─────────────────────────────────────────────────────────────

  getPreferences(): WidgetPreferences {
    return this.snapshot.preferences
  }

  setPreferences(partial: Partial<WidgetPreferences>): void {
    const next: WidgetPreferences = { ...this.snapshot.preferences, ...partial }
    this.updateSnapshot({
      preferences: next,
      currentDisplayMode: this.snapshot.activeActionId === null
        ? this.resolveEffectiveDisplayMode(next)
        : this.snapshot.currentDisplayMode,
    })
    this.emit('prefs:changed', next)
    if (this.hostOnPreferencesChange) {
      try { this.hostOnPreferencesChange(next) } catch { /* ignore host callback errors */ }
    }
  }

  resetToInitialPreferences(): void {
    this.setPreferences(this.initialPreferences)
  }

  // ─── View management ─────────────────────────────────────────────────────────

  setView(view: WidgetView): void {
    this.updateSnapshot({ currentView: view })
  }

  // ─── Focus management (T013) ─────────────────────────────────────────────────

  recordActivator(el: Element | null | undefined): void {
    if (!el) { this.activatingElementRef = null; return }
    if (typeof WeakRef === 'function') {
      this.activatingElementRef = new WeakRef(el)
    } else {
      // Best-effort fallback for environments without WeakRef (rare in modern browsers)
      const anyRef: { deref(): Element | undefined } = { deref: () => el }
      this.activatingElementRef = anyRef as unknown as WeakRef<Element>
    }
  }

  restoreFocus(): void {
    const el = this.activatingElementRef?.deref()
    this.activatingElementRef = null
    if (el && typeof (el as HTMLElement).focus === 'function' && el.isConnected !== false) {
      try { (el as HTMLElement).focus() } catch { /* ignore focus errors */ }
    }
  }

  // ─── Destroy ─────────────────────────────────────────────────────────────────

  destroy(): void {
    this.listeners.clear()
    this.eventListeners.clear()
    this.activatingElementRef = null
  }
}

let globalRegistry: WidgetRegistry | null = null

export function getRegistry(): WidgetRegistry {
  if (!globalRegistry) globalRegistry = new WidgetRegistry()
  return globalRegistry
}

export function resetRegistryForTesting(): void {
  if (globalRegistry) globalRegistry.destroy()
  globalRegistry = null
}

export { BUILT_IN_IDS }
